import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import {
  CREDIT_TIMEZONE,
  ERROR_CODES,
  pack12ExpiresAt,
  startOfDayIso,
  type ApiErrorBody,
} from '@icebreaker/shared';
import { selectStaff } from '@/features/auth/store/auth.selectors';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  forgetWriteKey,
  readStoredWriteKey,
  signatureDigest,
  storeWriteKey,
} from '../model/write-key-storage';
import { writeKeyIssued, writeKeyReleased } from '../store/write-keys.slice';
import styles from './organizations.module.css';

/**
 * P4/P6 DİYALOGLARININ ORTAK PARÇALARI. Katmanın kendisi kitten gelir
 * (`Modal`: 16 px köşe, lg gölge, odak tuzağı); burada yalnız referansın
 * diyalog içi blokları durur: alt başlık, bilgi bloğu, sunucu hatası ve
 * "kim adına yazılır" satırı.
 */

export function DialogSub({ children }: { children: ReactNode }) {
  return <p className={styles.dialogSub}>{children}</p>;
}

export function InfoBlock({ tone, children }: { tone: 'info' | 'warn'; children: ReactNode }) {
  return (
    <p className={[styles.info, tone === 'warn' ? styles.infoWarn : ''].filter(Boolean).join(' ')}>
      {children}
    </p>
  );
}

/** Bant görünümü: ton ve P6r'nin kalın bant ölçeği (`strong`). */
interface BannerLook {
  /** `warn` "hiçbir şey yazılmadı, başka yoldan devam edebilirsin" ya da "kayıt olmuş olabilir" içindir. */
  readonly tone?: 'danger' | 'warn';
  /**
   * P6r bantları 13 px kalındır (`font-weight:600`); P4/P6'nın öbür
   * diyalogları 12 px düz kalır, bu yüzden varsayılan değişmez.
   */
  readonly strong?: boolean;
}

function bannerClass({ tone = 'danger', strong = false }: BannerLook): string {
  return [
    styles.serverBanner,
    tone === 'warn' ? styles.serverBannerWarn : '',
    strong ? styles.serverBannerStrong : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** Kesin ret bandı. */
export function ServerBanner({ children, ...look }: BannerLook & { children: ReactNode }) {
  return (
    <p className={bannerClass(look)} role="alert" data-outcome="rejected">
      {children}
    </p>
  );
}

/** Oturumdaki personelin görünen adı (denetim satırı "… adına yazılır"). */
export function useStaffName(): string {
  const staff = useAppSelector(selectStaff);
  return staff?.account.displayName ?? staff?.account.email ?? '';
}

/** Alt şerit: solda denetim satırı, sağda eylemler (kit `Modal` sözleşmesi). */
export function DialogFooter({ audit, children }: { audit: string; children: ReactNode }) {
  return (
    <div className={styles.footer}>
      <p className={styles.audit}>{audit}</p>
      <div className={styles.footerActions}>{children}</div>
    </div>
  );
}

/**
 * YİNELEME ANAHTARI — çift basış ve kopma sonrası yeniden gönderim = tek kayıt
 * (Story D5 · T4 QA P2-1).
 *
 * Anahtar gönderim ANINDA seçilir: kapsamın (`grant:<kurum>` gibi) depodaki
 * anahtarı aynı gövde parmak izine aitse O kullanılır, değilse yenisi üretilip
 * depoya yazılır. Depo diyaloğun ömründen uzundur: yanıt koptuktan sonra
 * diyalog kapatılıp açılsa da aynı gövde aynı anahtarla gider ve sunucu ikinci
 * kaydı yazmaz. `release` başarıdan ya da kesin retten sonra çağrılır.
 *
 * Depo sayfa yenilemesinde sıfırlanır; anahtarın kalıcı kopyası
 * (`write-key-storage`, C17) yenilemeden sonra da aynı gövdeye aynı anahtarı verir.
 */
export function useWriteKey(scope: string): {
  readonly keyFor: (signature: string) => string;
  readonly release: () => void;
} {
  const dispatch = useAppDispatch();
  const entry = useAppSelector((root: RootState) => root.panelWriteKeys[scope]);
  const keyFor = useCallback(
    (signature: string) => {
      if (entry) {
        if (entry.signature === signature) return entry.key;
      } else {
        const stored = readStoredWriteKey(scope);
        if (stored && stored.digest === signatureDigest(signature)) return stored.key;
      }
      const key = newKey();
      dispatch(writeKeyIssued({ scope, signature, key }));
      storeWriteKey(scope, { digest: signatureDigest(signature), key });
      return key;
    },
    [dispatch, entry, scope],
  );
  const release = useCallback(() => {
    dispatch(writeKeyReleased(scope));
    forgetWriteKey(scope);
  }, [dispatch, scope]);
  return { keyFor, release };
}

export { failureKind, isOutcomeUnknown } from '../model/write-outcome';

/**
 * Anahtarı bırakma kuralı tek yerde: başarı ya da KESİN ret bırakır, sonucu
 * bilinmeyen hata korur (kapat-aç-yeniden gönder aynı anahtarla gider).
 */
export function useReleaseKeyOnSettle(
  phase: string,
  outcome: 'none' | 'unknown' | 'rejected',
  release: () => void,
): void {
  useEffect(() => {
    if (phase === 'succeeded' || (phase === 'failed' && outcome !== 'unknown')) release();
  }, [outcome, phase, release]);
}

/**
 * Sonuç doğrulanamadığında çizilen tek cümle ailesi: kesin bir "kaydedilmedi"
 * YOK, ne yeniden yüklendiğini ve aynı bilgilerle göndermenin güvenli olup
 * olmadığını söyler.
 */
export function UnknownOutcome({ reloaded, ...look }: BannerLook & { reloaded: string }) {
  const t = useTranslation();
  return (
    <p className={bannerClass(look)} role="alert" data-outcome="unknown">
      {t.t('admin.write.unknown')} {reloaded}
    </p>
  );
}

function newKey(): string {
  return globalThis.crypto.randomUUID();
}

/** Sunucu yanıtı alan hatası mı (400 + `fieldErrors`)? */
export function fieldErrorPaths(error: ApiErrorBody | null): ReadonlySet<string> {
  return new Set((error?.fieldErrors ?? []).map((item) => item.path));
}

/** Yineleme anahtarının başka gövdeyle yeniden kullanımı (409). */
export function isConflict(error: ApiErrorBody | null): boolean {
  return error?.code === ERROR_CODES.CONFLICT;
}

/** Bugünün takvim günü (`YYYY-MM-DD`, İstanbul — kredi kurallarının saati). */
export function useTodayInIstanbul(): string {
  return useMemo(() => istanbulDay(new Date(Date.now())), []);
}

export function istanbulDay(at: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** Takvim gününden bir yıl öncesi (29 Şubat → 28 Şubat). */
export function oneYearBefore(day: string): string {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const candidate = new Date(Date.UTC(year - 1, month - 1, date));
  if (candidate.getUTCMonth() !== month - 1) candidate.setUTCDate(0);
  return candidate.toISOString().slice(0, 10);
}

/**
 * Satın alma günü → 12'li paketin yanma ANI. Kural paylaşılan pakettedir
 * (`pack12ExpiresAt`, sunucunun hesapladığıyla aynı fonksiyon): onay cümlesi
 * sunucunun yazacağı bitişi söyler, istemcide ikinci bir takvim kuralı yok.
 */
export function pack12BurnInstant(purchaseDay: string): Date {
  return pack12ExpiresAt(new Date(startOfDayIso(purchaseDay, CREDIT_TIMEZONE)));
}

/** Basit ve hoşgörülü e-posta denetimi; asıl kural sunucudaki Joi şemasıdır. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}
